import type { AgentWidgetConfig, VoiceConfig } from "../types";

/** Changes that require replacing a session's capture and playback provider. */
export function voiceConnectionChanged(previous: AgentWidgetConfig, next: AgentWidgetConfig): boolean {
  const before = previous.voiceRecognition;
  const after = next.voiceRecognition;
  return (before?.enabled === true) !== (after?.enabled === true) ||
    before?.provider?.type !== after?.provider?.type ||
    before?.provider?.custom !== after?.provider?.custom ||
    usesSessionVoiceForConfig(previous) !== usesSessionVoiceForConfig(next) ||
    before?.provider?.runtype?.createPlaybackEngine !== after?.provider?.runtype?.createPlaybackEngine ||
    JSON.stringify(before?.provider?.runtype) !== JSON.stringify(after?.provider?.runtype) ||
    previous.agentId !== next.agentId || previous.clientToken !== next.clientToken || previous.apiUrl !== next.apiUrl;
}

/** Providers whose microphone lifecycle is owned by ChatSession instead of browser dictation. */
export function usesSessionVoice(config?: Partial<VoiceConfig>): boolean {
  return config?.type === "runtype" || config?.type === "custom";
}

/**
 * Eager, construction-free replica of the voice factory's `isVoiceSupported`
 * (`voice/voice-factory.ts`, which ships in the lazy voice-runtime chunk).
 * Drives mic-button visibility on first paint, so it must stay synchronous
 * and in the core bundle without dragging the providers in.
 *
 * Parity contract (asserted by `voice-support.test.ts`): returns exactly what
 * `isVoiceSupported` in the factory returns for every config shape —
 *  - `custom` with a provider/factory: resolve + shape-check (invoking a
 *    factory, as the factory version does via construction);
 *  - `runtype` with config: supported (the provider constructor only stores
 *    config; the WebSocket opens lazily);
 *  - otherwise: browser SpeechRecognition presence.
 */
export const isBrowserSpeechRecognitionSupported = (): boolean =>
  typeof window !== "undefined" &&
  ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

export function isVoiceSupportedProbe(config?: Partial<VoiceConfig>): boolean {
  try {
    if (config?.type === "custom" && config.custom) {
      const custom = config.custom;
      const provider = typeof custom === "function" ? custom() : custom;
      return !!provider && typeof provider.startListening === "function";
    }
    if (config?.type === "runtype" && config.runtype) return true;
    return isBrowserSpeechRecognitionSupported();
  } catch {
    return false;
  }
}

/** Custom STT review is owned by the composer; a separate dictation provider can coexist with voice. */
export function usesSessionVoiceForConfig(config: AgentWidgetConfig): boolean {
  const voice = config.voiceRecognition;
  return usesSessionVoice(voice?.provider) && !(voice?.provider?.type === "custom" &&
    !voice.dictation?.provider && (voice.dictation || voice.completionBehavior === "review"));
}
